import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Activity, AudioLines, CheckCircle2, Clock3, EyeOff, FileMusic, History, Loader2, Plus, Search, Trash2, Upload, Users, Video } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const tabs = [
  { id: "catalog", to: "/admin/music-catalog", label: "Catalogo" },
  { id: "review", to: "/admin/review", label: "Revision" },
  { id: "audit", to: "/admin/audit-log", label: "Actividad" },
];

const StatusBadge = ({ value = "draft" }) => {
  const style = value === "published" ? "bg-tune/15 text-tune" : value === "pending_review" ? "bg-brass/15 text-brass" : "bg-muted text-muted-foreground";
  return <Badge className={style}>{String(value).replace("_", " ")}</Badge>;
};
const formatDate = (value) => value ? new Date(value).toLocaleString("es-MX", { dateStyle: "medium", timeStyle: "short" }) : "--";
const activityLabel = (entityType) => ({ song_source: "video", analysis_profile: "timeline", global_song: "cancion" }[entityType] || entityType);

const AdminMusicCatalogPage = ({ section = "catalog" }) => {
  const { getAuthHeaders } = useAuth();
  const [songs, setSongs] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [queue, setQueue] = useState({ songs: [], profiles: [] });
  const [audit, setAudit] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (section === "catalog") {
        const [songResponse, dashboardResponse] = await Promise.all([
          axios.get(`${API_URL}/admin/songs`, { headers: getAuthHeaders(), params: query ? { q: query } : undefined }),
          axios.get(`${API_URL}/admin/dashboard`, { headers: getAuthHeaders() }),
        ]);
        setSongs(songResponse.data || []);
        setDashboard(dashboardResponse.data);
      } else if (section === "review") {
        const response = await axios.get(`${API_URL}/admin/review`, { headers: getAuthHeaders() });
        setQueue(response.data);
      } else {
        const response = await axios.get(`${API_URL}/admin/audit-log`, { headers: getAuthHeaders() });
        setAudit(response.data || []);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "No se pudo cargar el catalogo");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, query, section]);
  useEffect(() => { load(); }, [load]);

  const changeState = async (songId, action, title = "") => {
    try {
      if (action === "unpublish") {
        await axios.patch(`${API_URL}/admin/songs/${songId}/unpublish`, {}, { headers: getAuthHeaders() });
        toast.success("Practica despublicada — vuelve a borrador");
      } else if (action === "delete") {
        await axios.delete(`${API_URL}/admin/songs/${songId}`, { headers: getAuthHeaders() });
        toast.success(`"${title}" eliminada del catálogo`);
      } else {
        await axios.post(`${API_URL}/admin/songs/${songId}/${action}`, {}, { headers: getAuthHeaders() });
        toast.success(action === "publish" ? "Practica publicada" : "Cancion actualizada");
      }
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "No se pudo actualizar");
    }
  };

  return (
    <section className="steel-page space-y-5">
      <header className="flex flex-col gap-4 rounded-3xl border border-border bg-panel p-5 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-signal/65">Administration</p>
          <h1 className="mt-1 text-3xl font-bold text-foreground">Music Catalog Studio</h1>
          <p className="mt-1 text-sm text-muted-foreground">Canciones, videos, acordes, timelines y publicacion en un solo flujo musical.</p>
        </div>
        <div className="flex gap-2">
          <Link to="/admin/artist-import" className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl border border-signal/30 px-5 text-sm font-semibold text-signal hover:bg-signal/10">
            <Users className="h-4 w-4" /> Artist Import
          </Link>
          <Link to="/admin/bulk-import" className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl border border-signal/30 px-5 text-sm font-semibold text-signal hover:bg-signal/10">
            <Upload className="h-4 w-4" /> Bulk Import
          </Link>
          <Link to="/admin/music-catalog/new" className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-signal px-5 text-sm font-semibold text-ink hover:bg-signal/90">
            <Plus className="h-4 w-4" /> Crear practica
          </Link>
        </div>
      </header>
      <nav className="flex gap-1 rounded-2xl border border-border bg-panel/80 p-1">
        {tabs.map((tab) => <Link key={tab.id} to={tab.to} className={`rounded-xl px-4 py-2.5 text-sm font-semibold ${section === tab.id ? "bg-signal text-ink" : "text-muted-foreground"}`}>{tab.label}</Link>)}
      </nav>
      {loading ? <div className="grid min-h-64 place-items-center"><Loader2 className="h-7 w-7 animate-spin text-signal" /></div> : section === "catalog" ? (
        <>
          <DashboardCards totals={dashboard?.totals || {}} />
          <form onSubmit={(event) => { event.preventDefault(); load(); }} className="flex max-w-lg items-center gap-2 rounded-xl border border-border bg-ink/40 px-3">
            <Search className="h-4 w-4 text-muted-foreground" />
            <input value={query} onChange={(event) => setQuery(event.target.value)} className="h-12 flex-1 bg-transparent text-sm text-foreground outline-none" placeholder="Buscar cancion o artista" />
          </form>
          <SongCards songs={songs} onAction={changeState} />
        </>
      ) : section === "review" ? <ReviewQueue queue={queue} onAction={changeState} /> : <AuditRows rows={audit} />}
    </section>
  );
};

const DashboardCards = ({ totals }) => {
  const cards = [
    { label: "Canciones", value: totals.songs || 0, icon: FileMusic },
    { label: "Videos elegidos", value: totals.youtube_sources || 0, icon: Video },
    { label: "Sin acordes", value: totals.songs_without_chords || 0, icon: AudioLines },
    { label: "En revision", value: totals.pending_review || 0, icon: Clock3 },
    { label: "Publicadas", value: totals.published_profiles || 0, icon: CheckCircle2 },
  ];
  return <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">{cards.map(({ label, value, icon: Icon }) => <article key={label} className="rounded-2xl border border-border bg-panel p-4"><Icon className="mb-4 h-5 w-5 text-signal" /><p className="text-3xl font-bold text-foreground">{value}</p><p className="text-sm text-muted-foreground">{label}</p></article>)}</div>;
};

const SongCards = ({ songs, onAction }) => (
  <div className="grid gap-3 lg:grid-cols-2">
    {songs.map((song) => (
      <article key={song.id} className="rounded-2xl border border-border bg-panel p-4">
        <div className="flex justify-between gap-3">
          <div><h2 className="font-semibold text-foreground">{song.canonical_title}</h2><p className="text-sm text-muted-foreground">{song.canonical_artist || "Sin artista"} / {song.primary_key || "--"} / {song.primary_bpm || "--"} BPM</p></div>
          <StatusBadge value={song.status} />
        </div>
        <p className="mt-3 text-xs text-muted-foreground">Editada {formatDate(song.updated_at)}</p>
        <div className="mt-4 flex flex-wrap gap-2">
          <Link to={`/admin/music-catalog/${song.id}/edit`} className="rounded-lg bg-signal/15 px-3 py-2 text-sm font-semibold text-signal">Editar musica</Link>
          {song.status !== "published" && (
            <button type="button" onClick={() => onAction(song.id, "publish")} className="rounded-lg border border-tune/20 px-3 py-2 text-sm text-tune">
              Publicar
            </button>
          )}
          {song.status === "published" && (
            <button
              type="button"
              onClick={() => {
                if (window.confirm(`¿Despublicar "${song.canonical_title}"?\nVolverá a borrador (reversible).`)) {
                  onAction(song.id, "unpublish", song.canonical_title);
                }
              }}
              className="inline-flex items-center gap-1.5 rounded-lg border border-brass/20 px-3 py-2 text-sm text-brass hover:bg-brass/10"
            >
              <EyeOff className="h-3.5 w-3.5" /> Despublicar
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              if (window.confirm(`¿Eliminar "${song.canonical_title}"?\nEsta acción no se puede deshacer.`)) {
                onAction(song.id, "delete", song.canonical_title);
              }
            }}
            className="inline-flex items-center gap-1.5 rounded-lg border border-destructive/20 px-3 py-2 text-sm text-destructive hover:bg-destructive/10"
          >
            <Trash2 className="h-3.5 w-3.5" /> Eliminar
          </button>
        </div>
      </article>
    ))}
    {!songs.length && <p className="rounded-2xl border border-border p-8 text-center text-muted-foreground">No hay canciones todavia.</p>}
  </div>
);

const ReviewQueue = ({ queue, onAction }) => (
  <div className="space-y-3">
    <h2 className="flex items-center gap-2 text-lg font-semibold text-foreground"><Activity className="h-5 w-5 text-signal" /> Practicas listas para escuchar</h2>
    {(queue.songs || []).map((song) => <article key={song.id} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-brass/20 bg-brass/[0.06] p-4"><div><p className="font-semibold text-foreground">{song.canonical_title}</p><p className="text-sm text-muted-foreground">{song.canonical_artist || "--"}</p></div><div className="flex gap-2"><Link to={`/admin/music-catalog/${song.id}/edit`} className="rounded-lg border border-border px-4 py-2 text-sm text-muted-foreground">Escuchar</Link><button type="button" onClick={() => onAction(song.id, "publish")} className="rounded-lg bg-tune/20 px-4 py-2 text-sm text-tune">Publicar</button></div></article>)}
    {!(queue.songs || []).length && <p className="rounded-2xl border border-border p-8 text-center text-muted-foreground">No hay practicas pendientes de revision.</p>}
  </div>
);

const AuditRows = ({ rows }) => (
  <section className="rounded-2xl border border-border bg-panel p-4">
    <h2 className="mb-4 flex items-center gap-2 font-semibold text-foreground"><History className="h-4 w-4 text-signal" /> Actividad editorial</h2>
    <div className="space-y-2">{rows.map((row) => <p key={row.id} className="flex flex-wrap justify-between gap-3 rounded-xl border border-border bg-ink/40 p-3 text-sm text-muted-foreground"><span><strong>{row.action}</strong> {activityLabel(row.entity_type)}</span><span className="text-xs text-muted-foreground">{formatDate(row.created_at)}</span></p>)}</div>
  </section>
);

export default AdminMusicCatalogPage;
